import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import BoxPlotChart, { BoxPlotDataPoint } from '../ChartsView/BoxPlotChart';
import {
  DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_INCLUDE_BASELINE,
  DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_NUMBER_OF_RUNS,
  DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_SEED,
  DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_VARIATION,
  type BenchmarkAlgorithmSnapshot,
  type BenchmarkProfileCode,
  type BenchmarkMetricKey,
  type BenchmarkSummaryBasis,
  type SflagaImprovementBenchmarkReport,
  filterSflagaImprovementBenchmarkReport,
  runSflagaImprovementBenchmark,
} from '../../services/sflagaImprovementBenchmark';
import {
  RESULT_ALGORITHM_COLORS,
  RESULT_ALGORITHM_NAMES,
  type ResultAlgorithmKey,
} from '../../utils/algorithmRegistry';
import {
  clearSflagaImprovementBenchmarkHistory,
  getSflagaImprovementBenchmarkHistory,
  removeSflagaImprovementBenchmarkHistoryEntry,
  saveSflagaImprovementBenchmarkHistory,
  type SflagaImprovementBenchmarkHistoryEntry,
} from '../../utils/sflagaImprovementBenchmarkHistory';
import {
  downloadSflagaImprovementBenchmarkJson,
  exportSflagaImprovementBenchmarkToExcel,
} from '../../utils/sflagaImprovementBenchmarkExport';
import { getTopRankedLeaderGroup } from '../../utils/sflagaImprovementBenchmarkWinnerCounts';
import { getSflagaImprovementBenchmarkCompatibilityIssue } from '../../utils/sflagaImprovementBenchmarkSchema';

interface SflagaImprovementBenchmarkModalProps {
  open: boolean;
  onClose: () => void;
  datasetText?: string;
  defaultTaskCount?: number;
  defaultNumberOfRuns?: number;
  defaultParallelExecution?: boolean;
  benchmarkCode?: BenchmarkProfileCode;
  onNotify?: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info'
  ) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  tabIdPrefix: string;
  value: number;
}

const BENCHMARK_PROFILES = {
  g: {
    code: 'g',
    shortLabel: 'G',
    title: 'G Benchmark',
    description:
      'Compact SFLAGA improvement benchmark with distributions, runs, winners, diagnostics, export, history, and task-count comparison.',
    detailedPurpose:
      'Use the G Benchmark modal to measure how each proposed SFLAGA enhancement changes performance under one shared benchmark configuration. It runs the baseline condition, every single-enhancement-only condition, and the all-enabled condition across the full algorithm pipeline, then summarizes fitness, energy, cost, response time, execution time, convergence, and operation counts. This workspace is meant for controlled, fairness-focused comparison where the parameter budget stays fixed while the enhancement design changes.',
    parameterMode: 'shared' as const,
    statusCaption:
      'Includes `all disabled`, one-improvement-only conditions, and `all enabled`, with the same shared benchmark policy across the full algorithm pipeline.',
    compareCaption:
      'Compare the current benchmark with a second task count or any saved history entry to see how SFLAGA improvement effects shift with scale.',
    titleAccent: '#072f2c',
  },
  j: {
    code: 'j',
    shortLabel: 'J',
    title: 'J Benchmark',
    description:
      'Same benchmark workspace as G, but every run generates a fresh randomized shared parameter set before executing the full algorithm pipeline.',
    detailedPurpose:
      'Use the J Benchmark modal to test the same SFLAGA enhancement conditions under randomized shared benchmark settings instead of one fixed parameter budget. Each run generates a fresh shared set of iterations, population, selection rate, crossover rate, mutation rate, and memeplex count, then reuses that exact set across all algorithms and enhancement conditions in that run. This workspace is meant for robustness analysis, showing whether the observed SFLAGA improvement pattern still holds when the benchmark parameters vary widely from run to run.',
    parameterMode: 'randomized' as const,
    statusCaption:
      'Includes `all disabled`, one-improvement-only conditions, and `all enabled`, while reusing one freshly randomized shared parameter set across the full algorithm pipeline for each J run.',
    compareCaption:
      'Compare the current randomized-parameter benchmark with a second task count or any saved J history entry to see how the generated parameter set changes the outcome.',
    titleAccent: '#3b1406',
  },
} satisfies Record<
  BenchmarkProfileCode,
  {
    code: BenchmarkProfileCode;
    shortLabel: string;
    title: string;
    description: string;
    detailedPurpose: string;
    parameterMode: 'shared' | 'randomized';
    statusCaption: string;
    compareCaption: string;
    titleAccent: string;
  }
>;

const CONDITION_COLORS: Record<string, string> = {
  allDisabled: '#475569',
  guidedMutation: '#0f766e',
  elitePreservation: '#1d4ed8',
  similarityAwareDiversification: '#c2410c',
  adaptiveImmigrantRefresh: '#9333ea',
  allEnabled: '#166534',
};

const METRIC_META: Array<{
  key: BenchmarkMetricKey;
  label: string;
  better: 'higher' | 'lower';
  digits: number;
  unit?: string;
}> = [
  { key: 'fitness', label: 'Fitness', better: 'higher', digits: 6 },
  { key: 'energy', label: 'Energy', better: 'lower', digits: 3, unit: ' J' },
  { key: 'cost', label: 'Cost', better: 'lower', digits: 3 },
  { key: 'responseTime', label: 'Response Time', better: 'lower', digits: 2, unit: ' ms' },
  { key: 'executionTime', label: 'Execution Time', better: 'lower', digits: 2, unit: ' ms' },
];

const TAB_LABELS = [
  'Summary',
  'Runs',
  'Distribution',
  'Convergence',
  'Operations',
  'Compare',
] as const;

const flash = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(94, 234, 212, 0.7); transform: scale(1); }
  50% { box-shadow: 0 0 0 6px rgba(94, 234, 212, 0); transform: scale(1.03); }
  100% { box-shadow: 0 0 0 0 rgba(94, 234, 212, 0); transform: scale(1); }
`;

const flashBest = keyframes`
  0%, 100% {
    background-color: rgba(76, 175, 80, 0.10);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.35);
  }
  50% {
    background-color: rgba(76, 175, 80, 0.22);
    box-shadow: 0 0 8px 3px rgba(76, 175, 80, 0.35);
  }
`;

const flashWorst = keyframes`
  0%, 100% {
    background-color: rgba(211, 47, 47, 0.10);
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35);
  }
  50% {
    background-color: rgba(211, 47, 47, 0.22);
    box-shadow: 0 0 8px 3px rgba(211, 47, 47, 0.35);
  }
`;

type ExtremeState = 'best' | 'worst' | undefined;

function TabPanel({ children, value, index, tabIdPrefix }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${tabIdPrefix}-tabpanel-${index}`}
      aria-labelledby={`${tabIdPrefix}-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2.5 }}>{children}</Box>}
    </div>
  );
}

function prettifyConditionLabel(label: string): string {
  return label
    .replace('Similarity Diversification', 'Similarity')
    .replace('Adaptive Immigrant Refresh', 'Immigrant Refresh');
}

function getMetricMeta(metricKey: BenchmarkMetricKey) {
  return METRIC_META.find((metric) => metric.key === metricKey) ?? METRIC_META[0];
}

function isBenchmarkCancellationError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return /cancelled|canceled/i.test(error.message);
}

function getExtremeState(
  value: number,
  values: number[],
  better: 'higher' | 'lower'
): ExtremeState {
  const finiteValues = values.filter((candidate) => Number.isFinite(candidate));
  if (finiteValues.length < 2 || !Number.isFinite(value)) {
    return undefined;
  }

  const bestValue =
    better === 'higher' ? Math.max(...finiteValues) : Math.min(...finiteValues);
  const worstValue =
    better === 'higher' ? Math.min(...finiteValues) : Math.max(...finiteValues);

  if (value === bestValue && bestValue !== worstValue) {
    return 'best';
  }

  if (value === worstValue && bestValue !== worstValue) {
    return 'worst';
  }

  return undefined;
}

function getRankState(rank: number, total: number): ExtremeState {
  if (total < 2) {
    return undefined;
  }

  if (rank === 1) {
    return 'best';
  }

  if (rank === total) {
    return 'worst';
  }

  return undefined;
}

function getExtremeCellSx(state: ExtremeState) {
  if (state === 'best') {
    return {
      color: 'success.main',
      fontWeight: 700,
      backgroundColor: 'rgba(76, 175, 80, 0.10)',
      animation: `${flashBest} 1.5s ease-in-out infinite`,
      borderRadius: 1,
    };
  }

  if (state === 'worst') {
    return {
      color: 'error.main',
      fontWeight: 700,
      backgroundColor: 'rgba(211, 47, 47, 0.10)',
      animation: `${flashWorst} 1.5s ease-in-out infinite`,
      borderRadius: 1,
    };
  }

  return undefined;
}

function formatMetric(
  value: number,
  metricKey: BenchmarkMetricKey,
  digitsOverride?: number
): string {
  const meta = getMetricMeta(metricKey);
  const digits = digitsOverride ?? meta.digits;
  const unit = meta.unit ?? '';
  return `${value.toFixed(digits)}${unit}`;
}

function formatSharedParametersSummary(parameters: {
  initialPopulation: number;
  iterations: number;
  selectionRate: number;
  crossoverRate: number;
  mutationRate: number;
  memeplexCount: number;
}): string {
  return `Pop ${parameters.initialPopulation} • Iter ${parameters.iterations} • Sel ${parameters.selectionRate}% • Cross ${parameters.crossoverRate}% • Mut ${parameters.mutationRate}% • Mem ${parameters.memeplexCount}`;
}

function getSnapshotValue(
  snapshot: BenchmarkAlgorithmSnapshot,
  metricKey: BenchmarkMetricKey,
  basis: BenchmarkSummaryBasis
): number {
  return snapshot[metricKey][basis];
}

function getCrossConditionValue(
  row: { id: string; label: string; mean: number } | { id: string; label: string; median: number },
  basis: BenchmarkSummaryBasis
): number {
  return basis === 'mean' ? ('mean' in row ? row.mean : 0) : ('median' in row ? row.median : 0);
}

function renderAlgorithmChip(algorithmKey: string, algorithmName?: string) {
  const color =
    RESULT_ALGORITHM_COLORS[algorithmKey as ResultAlgorithmKey] ?? '#546e7a';

  return (
    <Chip
      size="small"
      label={
        algorithmName ??
        RESULT_ALGORITHM_NAMES[algorithmKey as ResultAlgorithmKey] ??
        algorithmKey
      }
      sx={{
        bgcolor: `${color}16`,
        color,
        border: `1px solid ${color}44`,
        fontWeight: 700,
      }}
    />
  );
}

function MetricCard(props: {
  title: string;
  value: string;
  subtitle: string;
  tone: string;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 3,
        minHeight: 116,
        background: `linear-gradient(160deg, ${props.tone} 0%, rgba(15,23,42,0.96) 100%)`,
        color: 'white',
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.78, letterSpacing: '0.04em' }}>
        {props.title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.4 }}>
        {props.value}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.8, opacity: 0.88 }}>
        {props.subtitle}
      </Typography>
    </Paper>
  );
}

function getHistoryLabel(entry: SflagaImprovementBenchmarkHistoryEntry): string {
  return `${entry.report.experiment.taskCount} tasks • ${new Date(
    entry.timestamp
  ).toLocaleString()}`;
}

const SflagaImprovementBenchmarkModal: React.FC<SflagaImprovementBenchmarkModalProps> = ({
  open,
  onClose,
  datasetText,
  defaultTaskCount = 100,
  defaultNumberOfRuns = DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_NUMBER_OF_RUNS,
  defaultParallelExecution = false,
  benchmarkCode = 'g',
  onNotify,
}) => {
  const profile = BENCHMARK_PROFILES[benchmarkCode];
  const tabIdPrefix = `${profile.code}-benchmark`;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const parallelExecutionSupported = typeof Worker !== 'undefined';
  const cancelRequestedRef = useRef(false);
  const [taskCount, setTaskCount] = useState(defaultTaskCount);
  const [taskCountInput, setTaskCountInput] = useState(String(defaultTaskCount));
  const [numberOfRuns, setNumberOfRuns] = useState(defaultNumberOfRuns);
  const [numberOfRunsInput, setNumberOfRunsInput] = useState(String(defaultNumberOfRuns));
  const [parallelExecution, setParallelExecution] = useState(defaultParallelExecution);
  const [compareTaskCount, setCompareTaskCount] = useState(defaultTaskCount * 2);
  const [compareTaskCountInput, setCompareTaskCountInput] = useState(
    String(defaultTaskCount * 2)
  );
  const [summaryBasis, setSummaryBasis] = useState<BenchmarkSummaryBasis>('mean');
  const [tab, setTab] = useState(0);
  const [runningMode, setRunningMode] = useState<'primary' | 'comparison' | null>(null);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<SflagaImprovementBenchmarkReport | null>(null);
  const [comparisonReport, setComparisonReport] =
    useState<SflagaImprovementBenchmarkReport | null>(null);
  const [historyEntries, setHistoryEntries] = useState<
    SflagaImprovementBenchmarkHistoryEntry[]
  >([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState('');
  const [datasetSourceLabel, setDatasetSourceLabel] =
    useState<string>('Uploaded dataset');
  const [comparisonSourceLabel, setComparisonSourceLabel] =
    useState<string>('History comparison');
  const [progressLabel, setProgressLabel] = useState<string>('');
  const [progressAlgorithmLabel, setProgressAlgorithmLabel] = useState<string>('');
  const [activeAlgorithmLabels, setActiveAlgorithmLabels] = useState<string[]>([]);
  const [progressImprovementLabel, setProgressImprovementLabel] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressRunNumber, setProgressRunNumber] = useState(0);
  const [progressTotalRuns, setProgressTotalRuns] = useState(0);
  const [progressConditionIndex, setProgressConditionIndex] = useState(0);
  const [progressTotalConditions, setProgressTotalConditions] = useState(0);
  const [selectedConditionId, setSelectedConditionId] = useState('');
  const [selectedParameterSet, setSelectedParameterSet] = useState<string>('all');
  const [distributionMetric, setDistributionMetric] =
    useState<BenchmarkMetricKey>('executionTime');
  const [compareMetric, setCompareMetric] =
    useState<BenchmarkMetricKey>('fitness');

  const normalizePositiveInteger = useCallback(
    (value: string | number, fallback: number): number => {
      const parsed = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(parsed) || parsed < 1) {
        return Math.max(1, Math.trunc(fallback || 1));
      }
      return Math.max(1, Math.trunc(parsed));
    },
    []
  );

  const refreshHistory = useCallback(() => {
    setHistoryEntries(getSflagaImprovementBenchmarkHistory(profile.code));
  }, [profile.code]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setTaskCount(defaultTaskCount);
    setTaskCountInput(String(defaultTaskCount));
    setNumberOfRuns(defaultNumberOfRuns);
    setNumberOfRunsInput(String(defaultNumberOfRuns));
    setParallelExecution(defaultParallelExecution);
    setCompareTaskCount(defaultTaskCount * 2);
    setCompareTaskCountInput(String(defaultTaskCount * 2));
    setSummaryBasis('mean');
    setTab(0);
    cancelRequestedRef.current = false;
    setCancelRequested(false);
    setError(null);
    setReport(null);
    setComparisonReport(null);
    setSelectedHistoryId('');
    setSelectedConditionId('');
    setSelectedParameterSet('all');
    setProgressLabel('');
    setProgressAlgorithmLabel('');
    setActiveAlgorithmLabels([]);
    setProgressImprovementLabel('');
    setProgressPercent(0);
    setProgressRunNumber(0);
    setProgressTotalRuns(0);
    setProgressConditionIndex(0);
    setProgressTotalConditions(0);
    refreshHistory();
  }, [open, defaultParallelExecution, defaultTaskCount, defaultNumberOfRuns, refreshHistory]);

  const parameterSetOptions = useMemo(() => {
    if (!report) {
      return [];
    }

    const runParameters =
      report.experiment.runParameters ??
      report.conditions[0]?.runs.map((run) => ({
        runNumber: run.runNumber,
        parameterSeed: run.parameterSeed,
        sharedParameters: run.sharedParameters ?? report.experiment.sharedParameters,
      })) ??
      [];

    return runParameters.map((run) => ({
      value: String(run.runNumber),
      label:
        profile.code === 'j'
          ? `Set ${run.runNumber}`
          : `Run ${run.runNumber}`,
      description: `Pop ${run.sharedParameters.initialPopulation} • Iter ${run.sharedParameters.iterations} • Sel ${run.sharedParameters.selectionRate}% • Cross ${run.sharedParameters.crossoverRate}% • Mut ${run.sharedParameters.mutationRate}% • Mem ${run.sharedParameters.memeplexCount}`,
    }));
  }, [profile.code, report]);

  const displayedReport = useMemo(() => {
    if (!report) {
      return null;
    }

    if (profile.code !== 'j' || selectedParameterSet === 'all') {
      return report;
    }

    return filterSflagaImprovementBenchmarkReport(report, [
      Number(selectedParameterSet),
    ]);
  }, [profile.code, report, selectedParameterSet]);

  const selectedParameterOption = useMemo(() => {
    if (profile.code !== 'j' || selectedParameterSet === 'all') {
      return null;
    }

    return (
      parameterSetOptions.find((option) => option.value === selectedParameterSet) ??
      null
    );
  }, [parameterSetOptions, profile.code, selectedParameterSet]);

  useEffect(() => {
    if (profile.code !== 'j' || selectedParameterSet === 'all') {
      return;
    }

    if (!parameterSetOptions.some((option) => option.value === selectedParameterSet)) {
      setSelectedParameterSet('all');
    }
  }, [parameterSetOptions, profile.code, selectedParameterSet]);

  useEffect(() => {
    if (!displayedReport) {
      return;
    }

    if (!selectedConditionId) {
      setSelectedConditionId(displayedReport.conditions[0]?.id ?? '');
      return;
    }

    if (
      !displayedReport.conditions.some(
        (condition) => condition.id === selectedConditionId
      )
    ) {
      setSelectedConditionId(displayedReport.conditions[0]?.id ?? '');
    }
  }, [displayedReport, selectedConditionId]);

  useEffect(() => {
    if (!selectedHistoryId) {
      return;
    }

    const entry = historyEntries.find((candidate) => candidate.id === selectedHistoryId);
    if (!entry) {
      return;
    }

    if (report) {
      const compatibilityIssue = getSflagaImprovementBenchmarkCompatibilityIssue(
        report,
        entry.report
      );

      if (compatibilityIssue) {
        setComparisonReport(null);
        setComparisonSourceLabel('');
        setSelectedHistoryId('');
        onNotify?.(
          `Saved benchmark comparison blocked: ${compatibilityIssue}`,
          'warning'
        );
        return;
      }
    }

    setComparisonReport(entry.report);
    setComparisonSourceLabel(`History • ${entry.datasetSourceLabel}`);
  }, [historyEntries, onNotify, report, selectedHistoryId]);

  const historyCompatibilityIssues = useMemo(() => {
    const issues = new Map<string, string>();

    if (!report) {
      return issues;
    }

    historyEntries.forEach((entry) => {
      const issue = getSflagaImprovementBenchmarkCompatibilityIssue(
        report,
        entry.report
      );
      if (issue) {
        issues.set(entry.id, issue);
      }
    });

    return issues;
  }, [historyEntries, report]);

  const activeCondition = useMemo(() => {
    if (!displayedReport) {
      return null;
    }

    return (
      displayedReport.conditions.find((condition) => condition.id === selectedConditionId) ??
      displayedReport.conditions[0] ??
      null
    );
  }, [displayedReport, selectedConditionId]);

  const currentCrossConditionRanking = useMemo(() => {
    if (!displayedReport) {
      return null;
    }

    return summaryBasis === 'mean'
      ? displayedReport.crossConditionSflagaRankingByMean
      : displayedReport.crossConditionSflagaRankingByMedian;
  }, [displayedReport, summaryBasis]);

  const currentWinnerCounts = useMemo(() => {
    if (!displayedReport) {
      return null;
    }

    return summaryBasis === 'mean'
      ? displayedReport.winnerCountsByMean
      : displayedReport.winnerCountsByMedian;
  }, [displayedReport, summaryBasis]);

  const summaryCards = useMemo(() => {
    if (!displayedReport || !currentCrossConditionRanking || !currentWinnerCounts) {
      return [];
    }

    const bestFitnessCondition = currentCrossConditionRanking.fitness[0];
    const bestEnergyCondition = currentCrossConditionRanking.energy[0];
    const fastestCondition = currentCrossConditionRanking.executionTime[0];
    const leader = currentWinnerCounts.overall[0];

    return [
      {
        title: `Best SFLAGA Fitness (${summaryBasis})`,
        value: formatMetric(
          getCrossConditionValue(bestFitnessCondition, summaryBasis),
          'fitness'
        ),
        subtitle: prettifyConditionLabel(bestFitnessCondition.label),
        tone: '#0f766e',
      },
      {
        title: `Lowest SFLAGA Energy (${summaryBasis})`,
        value: formatMetric(
          getCrossConditionValue(bestEnergyCondition, summaryBasis),
          'energy'
        ),
        subtitle: prettifyConditionLabel(bestEnergyCondition.label),
        tone: '#1d4ed8',
      },
      {
        title: `Fastest SFLAGA Variant (${summaryBasis})`,
        value: formatMetric(
          getCrossConditionValue(fastestCondition, summaryBasis),
          'executionTime'
        ),
        subtitle: prettifyConditionLabel(fastestCondition.label),
        tone: '#9333ea',
      },
      {
        title: `Top Overall Winner Count (${summaryBasis})`,
        value: `${leader?.winCount ?? 0} wins`,
        subtitle: leader?.algorithmName ?? 'No data',
        tone: '#92400e',
      },
    ];
  }, [currentCrossConditionRanking, currentWinnerCounts, displayedReport, summaryBasis]);

  const winnerCountChartRows = useMemo(() => {
    if (!currentWinnerCounts) {
      return [];
    }

    return currentWinnerCounts.overall.map((row) => ({
      algorithmName: row.algorithmName,
      winCount: row.winCount,
      fill:
        RESULT_ALGORITHM_COLORS[row.algorithmKey as ResultAlgorithmKey] ?? '#64748b',
    }));
  }, [currentWinnerCounts]);

  const conditionSummaryRows = useMemo(() => {
    if (!displayedReport) {
      return [];
    }

    return displayedReport.conditions.map((condition) => {
      const leadersByMetric = {} as Record<
        BenchmarkMetricKey,
        ReturnType<typeof getTopRankedLeaderGroup>
      >;

      (
        ['fitness', 'energy', 'cost', 'responseTime', 'executionTime'] as BenchmarkMetricKey[]
      ).forEach((metricKey) => {
        leadersByMetric[metricKey] = getTopRankedLeaderGroup(
          condition,
          metricKey,
          summaryBasis
        );
      });

      return {
        condition,
        leadersByMetric,
      };
    });
  }, [displayedReport, summaryBasis]);

  const runsTableRows = useMemo(() => {
    if (!activeCondition) {
      return [];
    }

    return activeCondition.runs.flatMap((run) =>
      Object.values(run.algorithms)
        .filter((algorithm): algorithm is NonNullable<typeof algorithm> => Boolean(algorithm))
        .map((algorithm) => ({
          runNumber: run.runNumber,
          runSeed: run.runSeed,
          parameterSeed: run.parameterSeed,
          sharedParameters:
            run.sharedParameters ??
            displayedReport!.experiment.sharedParameters,
          algorithm,
        }))
    );
  }, [activeCondition, displayedReport]);

  const groupedRunCards = useMemo(() => {
    if (!activeCondition || !displayedReport) {
      return [];
    }

    return activeCondition.runs.map((run) => {
      const algorithms = Object.values(run.algorithms)
        .filter((algorithm): algorithm is NonNullable<typeof algorithm> => Boolean(algorithm))
        .sort((a, b) => b.fitness - a.fitness);

      const metricWinners = METRIC_META.map((metric) => {
        const sorted = [...algorithms].sort((a, b) => {
          const aValue = a[metric.key];
          const bValue = b[metric.key];
          return metric.better === 'higher' ? bValue - aValue : aValue - bValue;
        });

        const winner = sorted[0];
        const worst = sorted[sorted.length - 1];

        return {
          metricKey: metric.key,
          metricLabel: metric.label,
          better: metric.better,
          winner,
          worst,
        };
      });

      return {
        runNumber: run.runNumber,
        runSeed: run.runSeed,
        parameterSeed: run.parameterSeed,
        sharedParameters:
          run.sharedParameters ?? displayedReport.experiment.sharedParameters,
        algorithms,
        metricWinners,
      };
    });
  }, [activeCondition, displayedReport]);

  const distributionRows = useMemo(() => {
    if (!activeCondition) {
      return [];
    }

    return Object.entries(activeCondition.algorithms)
      .filter((entry): entry is [string, BenchmarkAlgorithmSnapshot] => Boolean(entry[1]))
      .map(([algorithmKey, snapshot]) => ({
        algorithmKey,
        algorithmName:
          RESULT_ALGORITHM_NAMES[algorithmKey as ResultAlgorithmKey] ?? algorithmKey,
        snapshot,
      }));
  }, [activeCondition]);

  const boxPlotData = useMemo<BoxPlotDataPoint[]>(() => {
    return distributionRows.map((row) => ({
      name: row.algorithmName,
      min: row.snapshot[distributionMetric].min,
      q1: row.snapshot[distributionMetric].q1,
      median: row.snapshot[distributionMetric].median,
      q3: row.snapshot[distributionMetric].q3,
      max: row.snapshot[distributionMetric].max,
      mean: row.snapshot[distributionMetric].mean,
      stdDev: row.snapshot[distributionMetric].stdDev,
    }));
  }, [distributionMetric, distributionRows]);

  const singleRunDistributionRows = useMemo(() => {
    if (!activeCondition || activeCondition.runs.length !== 1) {
      return [];
    }

    const run = activeCondition.runs[0];

    return Object.entries(run.algorithms)
      .filter((entry): entry is [string, NonNullable<(typeof run.algorithms)[ResultAlgorithmKey]>] => Boolean(entry[1]))
      .map(([algorithmKey, algorithm]) => {
        let value = 0;

        switch (distributionMetric) {
          case 'fitness':
            value = algorithm.fitness;
            break;
          case 'energy':
            value = algorithm.energy;
            break;
          case 'cost':
            value = algorithm.cost;
            break;
          case 'responseTime':
            value = algorithm.responseTime;
            break;
          case 'executionTime':
            value = algorithm.executionTime;
            break;
          default:
            value = 0;
        }

        return {
          algorithmKey,
          algorithmName:
            RESULT_ALGORITHM_NAMES[algorithmKey as ResultAlgorithmKey] ?? algorithmKey,
          value,
          fill:
            RESULT_ALGORITHM_COLORS[algorithmKey as ResultAlgorithmKey] ?? '#64748b',
        };
      });
  }, [activeCondition, distributionMetric]);

  const distributionRankingRows = useMemo(() => {
    if (!activeCondition) {
      return [];
    }

    return summaryBasis === 'mean'
      ? activeCondition.rankingsByMean[distributionMetric]
      : activeCondition.rankingsByMedian[distributionMetric];
  }, [activeCondition, distributionMetric, summaryBasis]);

  const convergenceRows = useMemo(() => {
    if (!activeCondition) {
      return [];
    }

    const generationSet = new Set<number>();
    Object.values(activeCondition.convergenceByAlgorithm).forEach((points) => {
      (points ?? []).forEach((point) => {
        generationSet.add(point.generation);
      });
    });

    return Array.from(generationSet)
      .sort((a, b) => a - b)
      .map((generation) => {
        const row: Record<string, number | string> = { generation };

        Object.entries(activeCondition.convergenceByAlgorithm).forEach(
          ([algorithmKey, points]) => {
            const point = (points ?? []).find((candidate) => candidate.generation === generation);
            if (!point) {
              return;
            }

            row[algorithmKey] =
              summaryBasis === 'mean' ? point.mean : point.median;
          }
        );

        return row;
      });
  }, [activeCondition, summaryBasis]);

  const operationsRows = useMemo(() => {
    if (!activeCondition) {
      return [];
    }

    return Object.entries(activeCondition.operationStatistics)
      .filter((entry) => Boolean(entry[1]))
      .map(([algorithmKey, stats]) => ({
        algorithmKey,
        algorithmName:
          RESULT_ALGORITHM_NAMES[algorithmKey as ResultAlgorithmKey] ?? algorithmKey,
        executionSnapshot: activeCondition.algorithms[algorithmKey as ResultAlgorithmKey],
        stats: stats!,
        totals: activeCondition.runs.reduce(
          (accumulator, run) => {
            const algorithm =
              run.algorithms[algorithmKey as ResultAlgorithmKey];

            if (!algorithm) {
              return accumulator;
            }

            accumulator.runCount += 1;
            accumulator.fitnessEvaluations +=
              algorithm.operations.fitnessEvaluations;
            accumulator.crossoverCalls += algorithm.operations.crossoverCalls;
            accumulator.mutationCalls += algorithm.operations.mutationCalls;
            accumulator.localSearchCalls += algorithm.operations.localSearchCalls;

            return accumulator;
          },
          {
            runCount: 0,
            fitnessEvaluations: 0,
            crossoverCalls: 0,
            mutationCalls: 0,
            localSearchCalls: 0,
          }
        ),
      }));
  }, [activeCondition]);

  const compareConditionRows = useMemo(() => {
    if (!displayedReport || !comparisonReport) {
      return [];
    }

    const comparisonByConditionId = new Map(
      comparisonReport.conditions.map((condition) => [condition.id, condition])
    );

    return displayedReport.conditions
      .filter((condition) => comparisonByConditionId.has(condition.id))
      .map((condition) => {
        const comparisonCondition = comparisonByConditionId.get(condition.id)!;
        const currentValue = getSnapshotValue(
          condition.sflaga,
          compareMetric,
          summaryBasis
        );
        const nextValue = getSnapshotValue(
          comparisonCondition.sflaga,
          compareMetric,
          summaryBasis
        );

        return {
          id: condition.id,
          label: condition.label,
          currentValue,
          nextValue,
          delta: nextValue - currentValue,
          currentRank:
            (summaryBasis === 'mean'
              ? displayedReport.crossConditionSflagaRankingByMean[compareMetric]
              : displayedReport.crossConditionSflagaRankingByMedian[compareMetric]
            ).findIndex((row) => row.id === condition.id) + 1,
          nextRank:
            (summaryBasis === 'mean'
              ? comparisonReport.crossConditionSflagaRankingByMean[compareMetric]
              : comparisonReport.crossConditionSflagaRankingByMedian[compareMetric]
            ).findIndex((row) => row.id === condition.id) + 1,
        };
      });
  }, [compareMetric, comparisonReport, displayedReport, summaryBasis]);

  const runMetricValueMap = useMemo(() => {
    const valuesByRun = new Map<
      number,
      Record<
        'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime',
        number[]
      >
    >();

    runsTableRows.forEach((row) => {
      const runValues = valuesByRun.get(row.runNumber) ?? {
        fitness: [],
        energy: [],
        cost: [],
        responseTime: [],
        executionTime: [],
      };

      runValues.fitness.push(row.algorithm.fitness);
      runValues.energy.push(row.algorithm.energy);
      runValues.cost.push(row.algorithm.cost);
      runValues.responseTime.push(row.algorithm.responseTime);
      runValues.executionTime.push(row.algorithm.executionTime);

      valuesByRun.set(row.runNumber, runValues);
    });

    return valuesByRun;
  }, [runsTableRows]);

  const runOperationValueMap = useMemo(() => {
    const valuesByRun = new Map<
      number,
      Record<
        'fitnessEvaluations' | 'crossoverCalls' | 'mutationCalls' | 'localSearchCalls',
        number[]
      >
    >();

    runsTableRows.forEach((row) => {
      const runValues = valuesByRun.get(row.runNumber) ?? {
        fitnessEvaluations: [],
        crossoverCalls: [],
        mutationCalls: [],
        localSearchCalls: [],
      };

      runValues.fitnessEvaluations.push(row.algorithm.operations.fitnessEvaluations);
      runValues.crossoverCalls.push(row.algorithm.operations.crossoverCalls);
      runValues.mutationCalls.push(row.algorithm.operations.mutationCalls);
      runValues.localSearchCalls.push(row.algorithm.operations.localSearchCalls);

      valuesByRun.set(row.runNumber, runValues);
    });

    return valuesByRun;
  }, [runsTableRows]);

  const operationColumnValues = useMemo(
    () => ({
      executionMean: operationsRows.map(
        (row) => row.executionSnapshot?.executionTime.mean ?? 0
      ),
      executionMedian: operationsRows.map(
        (row) => row.executionSnapshot?.executionTime.median ?? 0
      ),
      fitnessEvaluations: operationsRows.map((row) => row.totals.fitnessEvaluations),
      crossoverCalls: operationsRows.map((row) => row.totals.crossoverCalls),
      mutationCalls: operationsRows.map((row) => row.totals.mutationCalls),
      localSearchCalls: operationsRows.map((row) => row.totals.localSearchCalls),
    }),
    [operationsRows]
  );

  const compareChartRows = useMemo(() => {
    return compareConditionRows.map((row) => ({
      condition: prettifyConditionLabel(row.label),
      current: row.currentValue,
      comparison: row.nextValue,
    }));
  }, [compareConditionRows]);

  const resolveDatasetText = async (): Promise<{
    content: string;
    sourceLabel: string;
  }> => {
    if (datasetText && datasetText.trim()) {
      return {
        content: datasetText,
        sourceLabel: 'Uploaded dataset',
      };
    }

    const response = await fetch('/QWS_Dataset.txt');
    if (!response.ok) {
      throw new Error('Failed to load the bundled QWS dataset');
    }

    return {
      content: await response.text(),
      sourceLabel: 'Bundled QWS dataset',
    };
  };

  const executeBenchmark = async (
    requestedTaskCount: number,
    mode: 'primary' | 'comparison'
  ) => {
    const normalizedTaskCount = Math.max(1, Math.trunc(requestedTaskCount || 0));
    const normalizedNumberOfRuns = Math.max(1, Math.trunc(numberOfRuns || 0));
    if (!Number.isFinite(normalizedTaskCount) || normalizedTaskCount < 1) {
      setError('Workflow task count must be at least 1.');
      return;
    }
    if (!Number.isFinite(normalizedNumberOfRuns) || normalizedNumberOfRuns < 1) {
      setError('Number of runs must be at least 1.');
      return;
    }

    setRunningMode(mode);
    cancelRequestedRef.current = false;
    setCancelRequested(false);
    setError(null);
    setProgressPercent(0);
    setProgressLabel('Preparing benchmark dataset...');
    setProgressAlgorithmLabel('');
    setActiveAlgorithmLabels([]);
    setProgressImprovementLabel('');
    setProgressRunNumber(0);
    setProgressTotalRuns(0);
    setProgressConditionIndex(0);
    setProgressTotalConditions(0);

    if (mode === 'primary') {
      setReport(null);
      setComparisonReport(null);
      setSelectedHistoryId('');
      setSelectedParameterSet('all');
    }

    try {
      const resolvedDataset = await resolveDatasetText();
      const parameterSeed =
        profile.parameterMode === 'randomized'
          ? Math.max(1, Math.trunc(Date.now() % 2147483647))
          : undefined;

      const nextReport = await runSflagaImprovementBenchmark({
        datasetText: resolvedDataset.content,
        taskCount: normalizedTaskCount,
        numberOfRuns: normalizedNumberOfRuns,
        parallelExecution,
        matrixSeed: DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_SEED,
        includeSflaGaBaseline: DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_INCLUDE_BASELINE,
        taskSpecificVariationEnabled: DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_VARIATION,
        benchmarkCode: profile.code,
        parameterMode: profile.parameterMode,
        parameterSeed,
        shouldCancel: () => cancelRequestedRef.current,
        onProgress: (update) => {
          setProgressPercent(update.percent);
          setProgressRunNumber(update.runNumber);
          setProgressTotalRuns(update.totalRuns);
          setProgressConditionIndex(update.conditionIndex);
          setProgressTotalConditions(update.totalConditions);
          setProgressLabel(
            `Condition ${update.conditionIndex}/${update.totalConditions} • run ${update.runNumber}/${update.totalRuns}`
          );
          setProgressImprovementLabel(update.improvementLabel);
          setProgressAlgorithmLabel(update.algorithmName ?? '');
          setActiveAlgorithmLabels((prev) => {
            if (!update.algorithmName) {
              return update.phase === 'summary' ? [] : prev;
            }

            if (update.phase === 'start') {
              return prev.includes(update.algorithmName)
                ? prev
                : [...prev, update.algorithmName];
            }

            if (update.phase === 'complete') {
              return prev.filter((label) => label !== update.algorithmName);
            }

            return [];
          });
        },
      });

      if (mode === 'primary') {
        setReport(nextReport);
        setDatasetSourceLabel(resolvedDataset.sourceLabel);
        setSelectedConditionId(nextReport.conditions[0]?.id ?? '');
        setTab(0);
      } else {
        setComparisonReport(nextReport);
        setComparisonSourceLabel(`Live run • ${resolvedDataset.sourceLabel}`);
        setSelectedHistoryId('');
        setTab(5);
      }

      saveSflagaImprovementBenchmarkHistory(
        nextReport,
        resolvedDataset.sourceLabel,
        profile.code
      );
      refreshHistory();
      setProgressPercent(100);
      setProgressLabel('Benchmark complete');
      setProgressImprovementLabel('');
      setProgressAlgorithmLabel('');
      setActiveAlgorithmLabels([]);
      onNotify?.(
        mode === 'primary'
          ? `${profile.shortLabel} benchmark completed and saved to history`
          : `${profile.shortLabel} comparison benchmark completed and saved to history`,
        'success'
      );
    } catch (runError) {
      if (isBenchmarkCancellationError(runError)) {
        setError(null);
        setProgressLabel('Benchmark cancelled');
        setProgressImprovementLabel('');
        setProgressAlgorithmLabel('');
        setActiveAlgorithmLabels([]);
        onNotify?.(
          mode === 'comparison'
            ? `${profile.shortLabel} comparison benchmark cancelled`
            : `${profile.shortLabel} benchmark cancelled`,
          'info'
        );
        return;
      }

      const message =
        runError instanceof Error
          ? runError.message
          : `Failed to run ${profile.shortLabel} benchmark`;
      setError(message);
      onNotify?.(message, 'error');
    } finally {
      cancelRequestedRef.current = false;
      setCancelRequested(false);
      setRunningMode(null);
    }
  };

  const handleCancelBenchmark = useCallback(() => {
    if (!runningMode || cancelRequestedRef.current) {
      return;
    }

    cancelRequestedRef.current = true;
    setCancelRequested(true);
    setProgressLabel('Cancellation requested. Waiting for the current benchmark step to stop...');
  }, [runningMode]);

  const handleDialogClose = (
    _event?: object,
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (runningMode && reason) {
      return;
    }

    if (!runningMode) {
      onClose();
    }
  };

  const currentMetricMeta = getMetricMeta(distributionMetric);
  const compareMetricMeta = getMetricMeta(compareMetric);
  const safeProgressPercent = Math.max(0, Math.min(100, progressPercent));
  const runPercent =
    progressTotalRuns > 0 ? Math.min(100, (progressRunNumber / progressTotalRuns) * 100) : 0;
  const conditionPercent =
    progressTotalConditions > 0
      ? Math.min(100, (progressConditionIndex / progressTotalConditions) * 100)
      : 0;
  const displayedProgressAlgorithmLabel =
    activeAlgorithmLabels.length === 0
      ? progressAlgorithmLabel
      : parallelExecution && activeAlgorithmLabels.length > 1
        ? activeAlgorithmLabels.join(', ')
        : activeAlgorithmLabels[activeAlgorithmLabels.length - 1];
  const activeAlgorithmColorLabel =
    activeAlgorithmLabels[activeAlgorithmLabels.length - 1] ?? progressAlgorithmLabel;
  const activeAlgorithmColor =
    RESULT_ALGORITHM_COLORS[
      (Object.entries(RESULT_ALGORITHM_NAMES).find(
        ([, label]) => label === activeAlgorithmColorLabel
      )?.[0] ?? '') as ResultAlgorithmKey
    ] ?? '#5eead4';

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" pr={4}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: profile.titleAccent,
              color: '#d5fff4',
            }}
          >
            <BoltIcon />
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {profile.title}
              </Typography>
              <Tooltip
                placement="top"
                arrow
                enterDelay={200}
                title={
                  <Box sx={{ maxWidth: 420, py: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                      {profile.title} purpose
                    </Typography>
                    <Typography variant="body2">
                      {profile.detailedPurpose}
                    </Typography>
                  </Box>
                }
              >
                <Box
                  component="span"
                  aria-label={`${profile.title} help`}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    cursor: 'help',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'text.primary',
                      borderColor: 'text.secondary',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  ?
                </Box>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {profile.description}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', lg: 'center' }}
          >
            <TextField
              type="number"
              size="small"
              label="Workflow Tasks"
              value={taskCountInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setTaskCountInput(nextValue);
                const parsed = Number(nextValue);
                if (Number.isFinite(parsed) && parsed > 0) {
                  setTaskCount(Math.trunc(parsed));
                }
              }}
              onBlur={(event) => {
                const normalized = normalizePositiveInteger(
                  event.target.value,
                  taskCount || defaultTaskCount
                );
                setTaskCount(normalized);
                setTaskCountInput(String(normalized));
              }}
              inputProps={{ min: 1, step: 1 }}
              disabled={Boolean(runningMode)}
              sx={{ minWidth: { xs: '100%', lg: 180 } }}
            />

            <TextField
              type="number"
              size="small"
              label="Runs"
              value={numberOfRunsInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setNumberOfRunsInput(nextValue);
                const parsed = Number(nextValue);
                if (Number.isFinite(parsed) && parsed > 0) {
                  setNumberOfRuns(Math.trunc(parsed));
                }
              }}
              onBlur={(event) => {
                const normalized = normalizePositiveInteger(
                  event.target.value,
                  numberOfRuns || defaultNumberOfRuns
                );
                setNumberOfRuns(normalized);
                setNumberOfRunsInput(String(normalized));
              }}
              inputProps={{ min: 1, step: 1 }}
              disabled={Boolean(runningMode)}
              sx={{ minWidth: { xs: '100%', lg: 140 } }}
            />

            <Tooltip
              title={
                parallelExecutionSupported
                  ? 'Run the benchmark algorithms in parallel using Web Workers to reduce wall-clock time when the browser can support it.'
                  : 'Web Workers are unavailable in this context, so benchmark runs will stay sequential.'
              }
            >
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={parallelExecution}
                      onChange={(event) => setParallelExecution(event.target.checked)}
                      disabled={Boolean(runningMode) || !parallelExecutionSupported}
                      size="small"
                    />
                  }
                  label="Parallel"
                  sx={{ ml: 0.25, mr: 0.5 }}
                />
              </Box>
            </Tooltip>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={summaryBasis}
              onChange={(_event, nextValue: BenchmarkSummaryBasis | null) => {
                if (nextValue) {
                  setSummaryBasis(nextValue);
                }
              }}
            >
              <ToggleButton value="mean">Mean</ToggleButton>
              <ToggleButton value="median">Median</ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() =>
                executeBenchmark(
                  normalizePositiveInteger(taskCountInput, taskCount),
                  'primary'
                )
              }
              disabled={Boolean(runningMode)}
            >
              Run
            </Button>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`${numberOfRuns} runs`}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`Matrix seed ${DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_SEED}`}
                variant="outlined"
              />
              <Chip
                size="small"
                label={
                  profile.parameterMode === 'randomized'
                    ? 'Random parameters'
                    : 'Shared parameters'
                }
                variant="outlined"
              />
              <Chip
                size="small"
                label={parallelExecution ? 'Parallel' : 'Sequential'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={datasetText?.trim() ? 'Uploaded dataset' : 'Bundled QWS dataset'}
                variant="outlined"
              />
            </Stack>

            <Tooltip title="Download JSON">
              <span>
                <IconButton
                  onClick={() =>
                    displayedReport &&
                    downloadSflagaImprovementBenchmarkJson(displayedReport)
                  }
                  disabled={!displayedReport || Boolean(runningMode)}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Download Excel">
              <span>
                <IconButton
                  onClick={() =>
                    displayedReport &&
                    exportSflagaImprovementBenchmarkToExcel(
                      displayedReport,
                      summaryBasis
                    )
                  }
                  disabled={!displayedReport || Boolean(runningMode)}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block' }}>
            {profile.statusCaption}
          </Typography>
          {parallelExecution ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {parallelExecutionSupported
                ? 'Parallel mode reduces wall-clock completion time, but execution-time comparisons become less strict because algorithms share CPU.'
                : 'Parallel mode is unavailable in this browser context, so this benchmark will run sequentially.'}
            </Typography>
          ) : null}
        </Paper>

        {(runningMode || progressLabel) && (
          <Paper
            sx={{
              mt: 2,
              p: 2.25,
              borderRadius: 3,
              background:
                runningMode === 'comparison'
                  ? 'linear-gradient(135deg, #1f1630 0%, #241b3a 100%)'
                  : 'linear-gradient(135deg, #0f172a 0%, #10243f 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'white',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.25,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'white',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                          Overall Progress
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {Math.round(safeProgressPercent)}%
                        </Typography>
                      </Stack>
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CircularProgress
                          variant="determinate"
                          value={Math.max(safeProgressPercent, runningMode ? 6 : 0)}
                          size={82}
                          thickness={5}
                          sx={{ color: '#5eead4' }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="subtitle2" color="white">
                            {progressRunNumber > 0 && progressTotalRuns > 0
                              ? `${progressRunNumber}/${progressTotalRuns}`
                              : 'Run'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        Condition Progress
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={conditionPercent}
                        sx={{
                          mt: 0.75,
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { backgroundColor: '#93c5fd' },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.75, color: 'rgba(255,255,255,0.84)', fontWeight: 600 }}
                      >
                        {progressConditionIndex > 0 && progressTotalConditions > 0
                          ? `Condition ${progressConditionIndex} / ${progressTotalConditions}`
                          : 'Preparing conditions'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        Run Progress
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <SpeedIcon fontSize="small" sx={{ color: '#5eead4' }} />
                        <Typography variant="body2" fontWeight={600}>
                          {progressRunNumber || 0} / {progressTotalRuns || '—'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ ml: 'auto', color: 'rgba(255,255,255,0.72)' }}
                        >
                          {Math.round(runPercent)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={runPercent}
                        sx={{
                          mt: 1,
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { backgroundColor: '#5eead4' },
                        }}
                      />
                    </Box>
                  </Paper>
                </Stack>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25,
                    height: '100%',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'white',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      gap: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {runningMode === 'comparison'
                          ? 'Comparison Benchmark Stream'
                          : runningMode === 'primary'
                            ? 'Benchmark Stream'
                            : 'Last Benchmark Status'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {progressLabel || 'Waiting for activity'}
                      </Typography>
                    </Box>
                    <Chip
                      label={runningMode ? 'Streaming' : 'Paused'}
                      color={runningMode ? 'success' : 'default'}
                      size="small"
                      sx={{ color: runningMode ? 'white' : 'rgba(255,255,255,0.8)' }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                      Overall Benchmark Progress
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={safeProgressPercent}
                      sx={{
                        mt: 0.75,
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#5eead4' },
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BoltIcon fontSize="small" sx={{ color: '#fcd34d' }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Active Stream
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {progressImprovementLabel ? (
                        <Chip
                          label={progressImprovementLabel}
                          size="small"
                          variant="outlined"
                          icon={<AutoAwesomeIcon fontSize="small" sx={{ color: '#fcd34d' }} />}
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.3)',
                            boxShadow: '0 0 0 1px rgba(252, 211, 77, 0.45)',
                            animation: runningMode ? `${flash} 1.6s ease-in-out infinite` : 'none',
                          }}
                        />
                      ) : (
                        <Chip
                          label="Waiting for improvement"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.2)' }}
                        />
                      )}
                      {displayedProgressAlgorithmLabel ? (
                        <Chip
                          label={displayedProgressAlgorithmLabel}
                          size="small"
                          variant="outlined"
                          icon={
                            <AutoAwesomeIcon
                              fontSize="small"
                              sx={{ color: activeAlgorithmColor }}
                            />
                          }
                          sx={{
                            color: 'white',
                            borderColor: activeAlgorithmColor,
                            boxShadow: `0 0 0 1px ${activeAlgorithmColor}`,
                            fontWeight: 700,
                            animation: runningMode ? `${flash} 1.6s ease-in-out infinite` : 'none',
                          }}
                        />
                      ) : (
                        <Chip
                          label="Waiting for algorithm"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.2)' }}
                        />
                      )}
                    </Stack>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: 'white',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                          Mode
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {runningMode === 'comparison'
                            ? 'Comparison'
                            : runningMode === 'primary'
                              ? 'Primary'
                              : 'Idle'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: 'white',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                          Current Condition
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {progressImprovementLabel || 'Preparing'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: 'white',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                          Current Algorithm
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {displayedProgressAlgorithmLabel || 'Preparing'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {displayedReport && (
          <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              sx={{ mt: 2.25 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Results
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {datasetSourceLabel} • {displayedReport.experiment.serviceCount} services • {new Date(
                    displayedReport.generatedAt
                  ).toLocaleString()}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Chip size="small" label={`${displayedReport.experiment.taskCount} tasks`} />
                <Chip size="small" label={`${displayedReport.experiment.numberOfRuns} runs`} />
                <Chip size="small" label={`${summaryBasis} basis`} variant="outlined" />
                <Chip
                  size="small"
                  label={
                    displayedReport.experiment.parallelExecution
                      ? 'Parallel'
                      : 'Sequential'
                  }
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={
                    (displayedReport.experiment.parameterMode ?? profile.parameterMode) ===
                    'randomized'
                      ? 'Random parameters'
                      : 'Shared parameters'
                  }
                  variant="outlined"
                />
                {displayedReport.experiment.parameterSeed ? (
                  <Chip
                    size="small"
                    label={`Base param seed ${displayedReport.experiment.parameterSeed}`}
                    variant="outlined"
                  />
                ) : null}
              </Stack>
            </Stack>

            {profile.code === 'j' && selectedParameterSet !== 'all' ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {selectedParameterOption?.description ?? 'Selected parameter set'}
              </Typography>
            ) : null}

            <Tabs
              value={tab}
              onChange={(_event, nextTab) => setTab(nextTab)}
              sx={{ mt: 1.5, borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TAB_LABELS.map((label, index) => (
                <Tab key={label} label={label} id={`${tabIdPrefix}-tab-${index}`} />
              ))}
            </Tabs>

            {profile.code === 'j' && selectedParameterOption ? (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: 'rgba(59, 130, 246, 0.05)',
                  borderColor: 'rgba(59, 130, 246, 0.22)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Active Parameter Set: {selectedParameterOption.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedParameterOption.description}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label="Applied across all tabs"
                    sx={{
                      bgcolor: 'rgba(59, 130, 246, 0.12)',
                      color: '#1d4ed8',
                      border: '1px solid rgba(59, 130, 246, 0.24)',
                    }}
                  />
                </Stack>
              </Paper>
            ) : null}

            <TabPanel value={tab} index={0} tabIdPrefix={tabIdPrefix}>
              <Grid container spacing={1.5}>
                {summaryCards.map((card) => (
                  <Grid item xs={12} sm={6} lg={3} key={card.title}>
                    <MetricCard {...card} />
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: 360 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                      Overall Winner Counts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cross-condition wins across all five metrics using the selected basis.
                    </Typography>
                    <Box sx={{ height: 285, mt: 1.5 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={winnerCountChartRows} margin={{ top: 6, right: 6, left: -20, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="algorithmName" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip />
                          <Bar dataKey="winCount" radius={[8, 8, 0, 0]}>
                            {winnerCountChartRows.map((row) => (
                              <Cell key={row.algorithmName} fill={row.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={8}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                      Condition Comparison
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Each leader shows the top value and, when available, the margin to the next distinct competitor on the selected summary basis.
                    </Typography>
                    <TableContainer sx={{ mt: 1.5, maxHeight: 420 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Condition</TableCell>
                            <TableCell>Enhancement Set</TableCell>
                            <TableCell>Fitness Leaders</TableCell>
                            <TableCell>Energy Leaders</TableCell>
                            <TableCell>Cost Leaders</TableCell>
                            <TableCell>Response Leaders</TableCell>
                            <TableCell>Execution Leaders</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {conditionSummaryRows.map(({ condition, leadersByMetric }) => (
                            <TableRow key={condition.id} hover>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor:
                                        CONDITION_COLORS[condition.id] ??
                                        theme.palette.primary.main,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {prettifyConditionLabel(condition.label)}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {condition.enabledEnhancementIds.join(', ') || 'None'}
                                </Typography>
                              </TableCell>
                              {(
                                ['fitness', 'energy', 'cost', 'responseTime', 'executionTime'] as BenchmarkMetricKey[]
                              ).map((metricKey) => {
                                const leaderGroup = leadersByMetric[metricKey];
                                const metricMeta = getMetricMeta(metricKey);
                                return (
                                  <TableCell
                                    key={`${condition.id}-${metricKey}`}
                                    sx={getExtremeCellSx(
                                      getExtremeState(
                                        leaderGroup.value ?? Number.NaN,
                                        conditionSummaryRows.map(
                                          ({ leadersByMetric: leaderSet }) =>
                                            leaderSet[metricKey].value ?? Number.NaN
                                        ),
                                        metricMeta.better
                                      )
                                    )}
                                  >
                                    <Stack spacing={0.6}>
                                      <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {leaderGroup.leaders.map((leader) => (
                                          <Box
                                            key={`${condition.id}-${metricKey}-${leader.algorithmKey}`}
                                            sx={{ mr: 1, mb: 0.5 }}
                                          >
                                            {renderAlgorithmChip(
                                              leader.algorithmKey,
                                              leader.algorithmName
                                            )}
                                          </Box>
                                        ))}
                                      </Stack>
                                      <Typography variant="caption" color="text.secondary">
                                        {leaderGroup.value !== null
                                          ? formatMetric(leaderGroup.value, metricKey)
                                          : 'N/A'}{' '}
                                        {leaderGroup.isTie ? '• tie' : ''}
                                        {leaderGroup.marginToNextDistinct !== null
                                          ? `• Δ ${formatMetric(
                                              leaderGroup.marginToNextDistinct,
                                              metricKey
                                            )}`
                                          : ''}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Winner Count Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tied first-place algorithms are each credited with a win.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        <TableCell>Leaders</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {METRIC_META.map((metric) => (
                        <TableRow key={metric.key} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{metric.label}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {(currentWinnerCounts?.byMetric[metric.key] ?? []).map((row) => (
                                <Chip
                                  key={`${metric.key}-${row.algorithmKey}`}
                                  size="small"
                                  label={`${row.algorithmName}: ${row.winCount}`}
                                  sx={{
                                    bgcolor: `${(
                                      RESULT_ALGORITHM_COLORS[
                                        row.algorithmKey as ResultAlgorithmKey
                                      ] ?? '#64748b'
                                    )}16`,
                                    ...(getExtremeCellSx(
                                      getExtremeState(
                                        row.winCount,
                                        (currentWinnerCounts?.byMetric[metric.key] ?? []).map(
                                          (candidate) => candidate.winCount
                                        ),
                                        'higher'
                                      )
                                    ) ?? {}),
                                  }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </TabPanel>

            <TabPanel value={tab} index={1} tabIdPrefix={tabIdPrefix}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                mb={1.5}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Raw Per-Run Results
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {profile.code === 'j' && parameterSetOptions.length > 0 ? (
                    <Select
                      size="small"
                      value={selectedParameterSet}
                      onChange={(event) => setSelectedParameterSet(event.target.value)}
                      sx={{ minWidth: 210 }}
                    >
                      <MenuItem value="all">All Parameter Sets</MenuItem>
                      {parameterSetOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                  <Select
                    size="small"
                    value={activeCondition?.id ?? ''}
                    onChange={(event) => setSelectedConditionId(event.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {displayedReport.conditions.map((condition) => (
                      <MenuItem key={condition.id} value={condition.id}>
                        {prettifyConditionLabel(condition.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Stack>
              <Alert severity="info" sx={{ mb: 2 }}>
                Runs are grouped by experiment run so each card gives you one clean analytical slice: the exact parameter set, the winners for the main metrics, and the detailed algorithm results for that run.
              </Alert>

              <Stack spacing={1.5}>
                {groupedRunCards.map((runGroup, index) => (
                  <Accordion
                    key={`run-card-${runGroup.runNumber}`}
                    defaultExpanded={index === 0}
                    disableGutters
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px !important',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: 'rgba(15, 23, 42, 0.03)',
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Stack spacing={1} sx={{ width: '100%' }}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', md: 'center' }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              Run #{runGroup.runNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              seed {runGroup.runSeed}
                              {runGroup.parameterSeed
                                ? ` • param seed ${runGroup.parameterSeed}`
                                : ''}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={`${runGroup.algorithms.length} algorithms`}
                            variant="outlined"
                          />
                        </Stack>

                        <Typography variant="body2" color="text.secondary">
                          {formatSharedParametersSummary(runGroup.sharedParameters)}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {runGroup.metricWinners.map((entry) => (
                            <Chip
                              key={`run-${runGroup.runNumber}-${entry.metricKey}`}
                              size="small"
                              label={`${entry.metricLabel}: ${entry.winner.algorithmName}`}
                              sx={{
                                bgcolor: `${(
                                  RESULT_ALGORITHM_COLORS[
                                    entry.winner.algorithmKey as ResultAlgorithmKey
                                  ] ?? '#64748b'
                                )}16`,
                              }}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 2 }}>
                      <Grid container spacing={1.5}>
                        {runGroup.metricWinners.map((entry) => (
                          <Grid item xs={12} sm={6} lg={4} key={`summary-${runGroup.runNumber}-${entry.metricKey}`}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: 2.5,
                                height: '100%',
                                background:
                                  'linear-gradient(180deg, rgba(15,23,42,0.02) 0%, rgba(15,23,42,0.00) 100%)',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {entry.metricLabel}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mt: 0.75 }}
                              >
                                {renderAlgorithmChip(
                                  entry.winner.algorithmKey,
                                  entry.winner.algorithmName
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatMetric(
                                    entry.winner[entry.metricKey],
                                    entry.metricKey
                                  )}
                                </Typography>
                              </Stack>
                              {entry.worst ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.75, display: 'block' }}
                                >
                                  Worst: {entry.worst.algorithmName} •{' '}
                                  {formatMetric(
                                    entry.worst[entry.metricKey],
                                    entry.metricKey
                                  )}
                                </Typography>
                              ) : null}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>

                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ borderRadius: 3, maxHeight: 420, mt: 2 }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Algorithm</TableCell>
                              <TableCell>Fitness</TableCell>
                              <TableCell>Energy</TableCell>
                              <TableCell>Cost</TableCell>
                              <TableCell>Response</TableCell>
                              <TableCell>Execution</TableCell>
                              <TableCell>Evals</TableCell>
                              <TableCell>Crossovers</TableCell>
                              <TableCell>Mutations</TableCell>
                              <TableCell>Local Search</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {runGroup.algorithms.map((algorithm) => (
                              <TableRow
                                key={`run-${runGroup.runNumber}-${algorithm.algorithmKey}`}
                                hover
                              >
                                <TableCell>
                                  {renderAlgorithmChip(
                                    algorithm.algorithmKey,
                                    algorithm.algorithmName
                                  )}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.fitness,
                                      runMetricValueMap.get(runGroup.runNumber)?.fitness ?? [],
                                      'higher'
                                    )
                                  )}
                                >
                                  {formatMetric(algorithm.fitness, 'fitness')}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.energy,
                                      runMetricValueMap.get(runGroup.runNumber)?.energy ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {formatMetric(algorithm.energy, 'energy')}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.cost,
                                      runMetricValueMap.get(runGroup.runNumber)?.cost ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {formatMetric(algorithm.cost, 'cost')}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.responseTime,
                                      runMetricValueMap.get(runGroup.runNumber)?.responseTime ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {formatMetric(algorithm.responseTime, 'responseTime')}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.executionTime,
                                      runMetricValueMap.get(runGroup.runNumber)?.executionTime ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {formatMetric(algorithm.executionTime, 'executionTime')}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.operations.fitnessEvaluations,
                                      runOperationValueMap.get(runGroup.runNumber)?.fitnessEvaluations ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {algorithm.operations.fitnessEvaluations}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.operations.crossoverCalls,
                                      runOperationValueMap.get(runGroup.runNumber)?.crossoverCalls ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {algorithm.operations.crossoverCalls}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.operations.mutationCalls,
                                      runOperationValueMap.get(runGroup.runNumber)?.mutationCalls ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {algorithm.operations.mutationCalls}
                                </TableCell>
                                <TableCell
                                  sx={getExtremeCellSx(
                                    getExtremeState(
                                      algorithm.operations.localSearchCalls,
                                      runOperationValueMap.get(runGroup.runNumber)?.localSearchCalls ?? [],
                                      'lower'
                                    )
                                  )}
                                >
                                  {algorithm.operations.localSearchCalls}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </TabPanel>

            <TabPanel value={tab} index={2} tabIdPrefix={tabIdPrefix}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                mb={1.5}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Select
                    size="small"
                    value={activeCondition?.id ?? ''}
                    onChange={(event) => setSelectedConditionId(event.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {displayedReport.conditions.map((condition) => (
                      <MenuItem key={condition.id} value={condition.id}>
                        {prettifyConditionLabel(condition.label)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    size="small"
                    value={distributionMetric}
                    onChange={(event) =>
                      setDistributionMetric(event.target.value as BenchmarkMetricKey)
                    }
                    sx={{ minWidth: 190 }}
                  >
                    {METRIC_META.map((metric) => (
                      <MenuItem key={metric.key} value={metric.key}>
                        {metric.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {activeCondition?.runs.length === 1
                    ? 'A single selected parameter set leaves one run per algorithm, so this view shows raw run values instead of a box plot.'
                    : 'Box plots expose spread, quartiles, and outliers better than means alone.'}
                </Typography>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                {activeCondition?.runs.length === 1 ? (
                  <Box sx={{ height: 420 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={singleRunDistributionRows}
                        margin={{ top: 12, right: 18, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="algorithmName" interval={0} angle={-12} textAnchor="end" height={70} />
                        <YAxis />
                        <RechartsTooltip
                          formatter={(value: number) => [
                            `${value.toFixed(
                              currentMetricMeta.key === 'fitness'
                                ? 6
                                : currentMetricMeta.digits
                            )}${currentMetricMeta.unit ?? ''}`,
                            currentMetricMeta.label,
                          ]}
                        />
                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                          {singleRunDistributionRows.map((row) => (
                            <Cell key={row.algorithmKey} fill={row.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <BoxPlotChart
                    data={boxPlotData}
                    height={420}
                    yAxisLabel={`${currentMetricMeta.label}${currentMetricMeta.unit ?? ''}`}
                    valueFormatter={(value) =>
                      value.toFixed(currentMetricMeta.key === 'fitness' ? 3 : currentMetricMeta.digits)
                    }
                    tooltipUnit={currentMetricMeta.unit ?? ''}
                    boxFill="#c8d8ef"
                    boxHoverFill="#b5cae8"
                    boxStroke="#3b82f6"
                  />
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Condition Ranking for {currentMetricMeta.label}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Rank</TableCell>
                        <TableCell>Algorithm</TableCell>
                        <TableCell>{summaryBasis === 'mean' ? 'Mean' : 'Median'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {distributionRankingRows.map((row, index) => (
                        <TableRow key={`${row.algorithmKey}-${distributionMetric}`} hover>
                          <TableCell sx={getExtremeCellSx(getRankState(index + 1, distributionRankingRows.length))}>
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {renderAlgorithmChip(row.algorithmKey, row.algorithmName)}
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.value,
                                distributionRankingRows.map((candidate) => candidate.value),
                                getMetricMeta(distributionMetric).better
                              )
                            )}
                          >
                            {formatMetric(row.value, distributionMetric)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </TabPanel>

            <TabPanel value={tab} index={3} tabIdPrefix={tabIdPrefix}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                mb={1.5}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Fitness Convergence by Condition
                </Typography>
                <Select
                  size="small"
                  value={activeCondition?.id ?? ''}
                  onChange={(event) => setSelectedConditionId(event.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {displayedReport.conditions.map((condition) => (
                    <MenuItem key={condition.id} value={condition.id}>
                      {prettifyConditionLabel(condition.label)}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: 420 }}>
                <Typography variant="caption" color="text.secondary">
                  The line for each algorithm uses {summaryBasis} best-fitness values aggregated across the benchmark runs for this condition.
                </Typography>
                <Box sx={{ height: 340, mt: 1.5 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={convergenceRows} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="generation" />
                      <YAxis domain={['auto', 'auto']} />
                      <RechartsTooltip />
                      <Legend />
                      {Object.entries(activeCondition?.convergenceByAlgorithm ?? {}).map(
                        ([algorithmKey]) => (
                          <Line
                            key={algorithmKey}
                            type="monotone"
                            dataKey={algorithmKey}
                            name={
                              RESULT_ALGORITHM_NAMES[algorithmKey as ResultAlgorithmKey] ??
                              algorithmKey
                            }
                            stroke={
                              RESULT_ALGORITHM_COLORS[
                                algorithmKey as ResultAlgorithmKey
                              ] ?? '#64748b'
                            }
                            strokeWidth={2}
                            dot={false}
                            connectNulls
                          />
                        )
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </TabPanel>

            <TabPanel value={tab} index={4} tabIdPrefix={tabIdPrefix}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {displayedReport.diagnostics.notes.join(' ')}
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Execution-Time Diagnostics
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip size="small" label={displayedReport.diagnostics.timingMethod} variant="outlined" />
                  <Chip size="small" label={displayedReport.diagnostics.measuredScope} variant="outlined" />
                  <Chip
                    size="small"
                    label={
                      displayedReport.diagnostics.resetsCalculatorsPerAlgorithm
                        ? 'Fresh calculators per algorithm'
                        : 'Shared calculator state'
                    }
                    variant="outlined"
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  justifyContent="space-between"
                  mb={1.5}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Operation Counts by Algorithm
                  </Typography>
                  <Select
                    size="small"
                    value={activeCondition?.id ?? ''}
                    onChange={(event) => setSelectedConditionId(event.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {displayedReport.conditions.map((condition) => (
                      <MenuItem key={condition.id} value={condition.id}>
                        {prettifyConditionLabel(condition.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}
                >
                  Operation columns show total counts for the currently displayed runs.
                  The smaller line under each total shows the average per run.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Algorithm</TableCell>
                        <TableCell>Execution Mean</TableCell>
                        <TableCell>Execution Median</TableCell>
                        <TableCell>Fitness Evals</TableCell>
                        <TableCell>Crossovers</TableCell>
                        <TableCell>Mutations</TableCell>
                        <TableCell>Local Search</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operationsRows.map((row) => (
                        <TableRow key={row.algorithmKey} hover>
                          <TableCell>
                            {renderAlgorithmChip(row.algorithmKey, row.algorithmName)}
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.executionSnapshot?.executionTime.mean ?? 0,
                                operationColumnValues.executionMean,
                                'lower'
                              )
                            )}
                          >
                            {formatMetric(
                              row.executionSnapshot?.executionTime.mean ?? 0,
                              'executionTime'
                            )}
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.executionSnapshot?.executionTime.median ?? 0,
                                operationColumnValues.executionMedian,
                                'lower'
                              )
                            )}
                          >
                            {formatMetric(
                              row.executionSnapshot?.executionTime.median ?? 0,
                              'executionTime'
                            )}
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.totals.fitnessEvaluations,
                                operationColumnValues.fitnessEvaluations,
                                'lower'
                              )
                            )}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.totals.fitnessEvaluations.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              avg {row.stats.fitnessEvaluations.mean.toFixed(1)}/run
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.totals.crossoverCalls,
                                operationColumnValues.crossoverCalls,
                                'lower'
                              )
                            )}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.totals.crossoverCalls.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              avg {row.stats.crossoverCalls.mean.toFixed(1)}/run
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.totals.mutationCalls,
                                operationColumnValues.mutationCalls,
                                'lower'
                              )
                            )}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.totals.mutationCalls.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              avg {row.stats.mutationCalls.mean.toFixed(1)}/run
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={getExtremeCellSx(
                              getExtremeState(
                                row.totals.localSearchCalls,
                                operationColumnValues.localSearchCalls,
                                'lower'
                              )
                            )}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.totals.localSearchCalls.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              avg {row.stats.localSearchCalls.mean.toFixed(1)}/run
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </TabPanel>

            <TabPanel value={tab} index={5} tabIdPrefix={tabIdPrefix}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Stack
                  direction={{ xs: 'column', xl: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', xl: 'center' }}
                >
                  <TextField
                    size="small"
                    type="number"
                    label="Compare Task Count"
                    value={compareTaskCountInput}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setCompareTaskCountInput(nextValue);
                      const parsed = Number(nextValue);
                      if (Number.isFinite(parsed) && parsed > 0) {
                        setCompareTaskCount(Math.trunc(parsed));
                      }
                    }}
                    onBlur={(event) => {
                      const normalized = normalizePositiveInteger(
                        event.target.value,
                        compareTaskCount || defaultTaskCount * 2
                      );
                      setCompareTaskCount(normalized);
                      setCompareTaskCountInput(String(normalized));
                    }}
                    inputProps={{ min: 1, step: 1 }}
                    disabled={Boolean(runningMode)}
                    sx={{ minWidth: { xs: '100%', xl: 180 } }}
                  />

                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PlayArrowIcon />}
                    onClick={() =>
                      executeBenchmark(
                        normalizePositiveInteger(
                          compareTaskCountInput,
                          compareTaskCount
                        ),
                        'comparison'
                      )
                    }
                    disabled={!displayedReport || Boolean(runningMode)}
                  >
                    Run Compare
                  </Button>

                  <Select
                    size="small"
                    value={selectedHistoryId}
                    displayEmpty
                    onChange={(event) => setSelectedHistoryId(event.target.value)}
                    sx={{ minWidth: { xs: '100%', xl: 300 } }}
                  >
                    <MenuItem value="">Compare with saved history entry</MenuItem>
                    {historyEntries.map((entry) => (
                      <MenuItem
                        key={entry.id}
                        value={entry.id}
                        disabled={historyCompatibilityIssues.has(entry.id)}
                      >
                        {getHistoryLabel(entry)}
                        {historyCompatibilityIssues.has(entry.id)
                          ? ' • incompatible benchmark version'
                          : ''}
                      </MenuItem>
                    ))}
                  </Select>

                  <Select
                    size="small"
                    value={compareMetric}
                    onChange={(event) =>
                      setCompareMetric(event.target.value as BenchmarkMetricKey)
                    }
                    sx={{ minWidth: { xs: '100%', xl: 180 } }}
                  >
                    {METRIC_META.map((metric) => (
                      <MenuItem key={metric.key} value={metric.key}>
                        {metric.label}
                      </MenuItem>
                    ))}
                  </Select>

                  <Box sx={{ flex: 1 }} />

                  <Tooltip title="Remove selected history entry">
                    <span>
                      <IconButton
                        disabled={!selectedHistoryId}
                        onClick={() => {
                          if (!selectedHistoryId) {
                            return;
                          }
                          removeSflagaImprovementBenchmarkHistoryEntry(selectedHistoryId);
                          setSelectedHistoryId('');
                          refreshHistory();
                          onNotify?.(
                            `${profile.shortLabel} benchmark history entry removed`,
                            'info'
                          );
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Clear benchmark history">
                    <span>
                      <IconButton
                        disabled={historyEntries.length === 0}
                        onClick={() => {
                          clearSflagaImprovementBenchmarkHistory(profile.code);
                          setSelectedHistoryId('');
                          refreshHistory();
                          onNotify?.(
                            `${profile.shortLabel} benchmark history cleared`,
                            'info'
                          );
                        }}
                      >
                        <DeleteSweepIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block' }}>
                  {profile.compareCaption}
                </Typography>
              </Paper>

              {comparisonReport && displayedReport ? (
                <>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                    <Chip
                      size="small"
                      label={`Current: ${displayedReport.experiment.taskCount} tasks`}
                    />
                    <Chip
                      size="small"
                      label={`Compare: ${comparisonReport.experiment.taskCount} tasks`}
                      variant="outlined"
                    />
                    <Chip size="small" label={comparisonSourceLabel} variant="outlined" />
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2, height: 380 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      SFLAGA Condition Shift: {compareMetricMeta.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current task count versus the comparison task count using the selected {summaryBasis} basis.
                    </Typography>
                    <Box sx={{ height: 300, mt: 1.5 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={compareChartRows} margin={{ top: 8, right: 12, left: -8, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="condition" interval={0} angle={-10} textAnchor="end" height={60} />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="current" name={`${displayedReport.experiment.taskCount} tasks`} fill="#0f766e" radius={[8, 8, 0, 0]} />
                          <Bar dataKey="comparison" name={`${comparisonReport.experiment.taskCount} tasks`} fill="#9333ea" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Condition Delta Table
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Condition</TableCell>
                            <TableCell>{displayedReport.experiment.taskCount} Tasks</TableCell>
                            <TableCell>{comparisonReport.experiment.taskCount} Tasks</TableCell>
                            <TableCell>Delta</TableCell>
                            <TableCell>Current Rank</TableCell>
                            <TableCell>Compare Rank</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {compareConditionRows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell sx={{ fontWeight: 700 }}>
                                {prettifyConditionLabel(row.label)}
                              </TableCell>
                              <TableCell
                                sx={getExtremeCellSx(
                                  getExtremeState(
                                    row.currentValue,
                                    compareConditionRows.map((candidate) => candidate.currentValue),
                                    compareMetricMeta.better
                                  )
                                )}
                              >
                                {formatMetric(row.currentValue, compareMetric)}
                              </TableCell>
                              <TableCell
                                sx={getExtremeCellSx(
                                  getExtremeState(
                                    row.nextValue,
                                    compareConditionRows.map((candidate) => candidate.nextValue),
                                    compareMetricMeta.better
                                  )
                                )}
                              >
                                {formatMetric(row.nextValue, compareMetric)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    compareMetricMeta.better === 'higher'
                                      ? row.delta >= 0
                                        ? 'success.main'
                                        : 'error.main'
                                      : row.delta <= 0
                                        ? 'success.main'
                                        : 'error.main',
                                  fontWeight: 700,
                                }}
                              >
                                {row.delta >= 0 ? '+' : ''}
                                {formatMetric(row.delta, compareMetric)}
                              </TableCell>
                              <TableCell
                                sx={getExtremeCellSx(
                                  getExtremeState(
                                    row.currentRank,
                                    compareConditionRows.map((candidate) => candidate.currentRank),
                                    'lower'
                                  )
                                )}
                              >
                                {row.currentRank}
                              </TableCell>
                              <TableCell
                                sx={getExtremeCellSx(
                                  getExtremeState(
                                    row.nextRank,
                                    compareConditionRows.map((candidate) => candidate.nextRank),
                                    'lower'
                                  )
                                )}
                              >
                                {row.nextRank}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Run a comparison task count or choose a saved history entry to activate the comparison view.
                </Alert>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.75 }}>
        <Button
          onClick={runningMode ? handleCancelBenchmark : onClose}
          color={runningMode ? 'error' : 'inherit'}
          disabled={Boolean(runningMode) && cancelRequested}
        >
          {runningMode
            ? cancelRequested
              ? 'Cancelling...'
              : `Cancel ${profile.shortLabel} Benchmark`
            : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SflagaImprovementBenchmarkModal;
